// Interpretation-layer logic ported 1:1 from the Streamlit app.py.
// These are UI repackaging helpers — the core financial model stays in the backend.

import {
  Classification, MetricRow, QualityFilter, ResearchQuestion, ScanData,
  StatusKind, Summary,
} from "../types";
import { FILTER_EXPLANATIONS, FILTER_TO_METRIC, INVESTOR_PROFILES, LOWER_IS_BETTER, MANUAL_FIELDS, YEAR_LABELS } from "./constants";

export function safeFloat(value: any, fallback = 0): number {
  const n = typeof value === "number" ? value : parseFloat(value);
  if (n === null || n === undefined || Number.isNaN(n) || !Number.isFinite(n)) return fallback;
  return n;
}

export function finiteOrNull(value: any): number | null {
  const n = typeof value === "number" ? value : parseFloat(value);
  if (n === null || n === undefined || Number.isNaN(n) || !Number.isFinite(n)) return null;
  return n;
}

export function statusOf(result: any): StatusKind {
  const text = String(result || "");
  for (const clean of ["Pass", "Fail", "Watch", "Missing"] as StatusKind[]) {
    if (text.includes(clean)) return clean;
  }
  return "Missing";
}

function countStatus(filters: QualityFilter[], status: StatusKind): number {
  return (filters || []).filter((f) => statusOf(f.Result) === status).length;
}

// ---------------------------------------------------------------------------
// Financify Score / Financify Valuation Score - continuous formulation.
//
// The original formula blended small discrete point buckets (0/1/2 per
// category) and a pass/fail rate across threshold checks for quality, and
// discretized each of 4 valuation multiples into Undervalued/Fair/Overvalued
// (+1/0/-1) for valuation - both throw away real magnitude before the score
// is even computed (a 21% ROE and a 45% ROE both just cleared the ">20%"
// bar identically; a stock 6% below its own historical PE and one 60% below
// both just got "+1"). This continuous version keeps the same underlying
// metrics but combines them as scaled/averaged continuous values instead of
// discrete buckets. Ported 1:1 from backend/honey_core.py's
// build_financify_stock_score/build_financify_valuation_score, which is the
// canonical implementation - verified against real historical data in
// research/continuous_scoring.py before being promoted to production.
// ---------------------------------------------------------------------------

function scaleScore(value: number | null, lo: number, hi: number): number | null {
  if (value === null) return null;
  const v = Math.max(lo, Math.min(hi, value));
  return ((v - lo) / (hi - lo)) * 100;
}

function scaleScoreInverse(value: number | null, lo: number, hi: number): number | null {
  const scaled = scaleScore(value, lo, hi);
  return scaled === null ? null : 100 - scaled;
}

function meanOfScores(values: (number | null)[]): number | null {
  const valid = values.filter((v): v is number => v !== null);
  return valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
}

/** Port of honey_core.py's build_financify_stock_score — continuous 0-100
 * quality score. `filters` is accepted but unused by the continuous formula
 * - kept only so existing call sites don't need to change shape. */
export function buildFinancifyStockScore(summary: Summary | null, _filters: QualityFilter[] | null): number {
  const s = summary || ({} as Summary);
  const roe = finiteOrNull(s.latest_roe);
  const roce = finiteOrNull(s.latest_roce);
  const patMargin = finiteOrNull(s.latest_pat_margin);
  const de = finiteOrNull(s.latest_de);
  const cfoPat = finiteOrNull(s.latest_cfo_pat);
  const retainedEarnPct = finiteOrNull(s.latest_retained_earn_pct);
  const epsCagrPct = finiteOrNull(s.eps_cagr_pct);

  const profitability = meanOfScores([scaleScore(roe, 0, 35), scaleScore(roce, 0, 35), scaleScore(patMargin, 0, 25)]);
  const growth = scaleScore(epsCagrPct, -20, 40);
  const debt = scaleScoreInverse(de, 0, 4);
  const cashQuality = meanOfScores([scaleScore(cfoPat, 0, 1.5), scaleScore(retainedEarnPct, 0, 100)]);

  const weighted: [number | null, number][] = [
    [profitability, 0.3],
    [growth, 0.25],
    [debt, 0.2],
    [cashQuality, 0.25],
  ];
  const present = weighted.filter((wv): wv is [number, number] => wv[0] !== null);
  if (!present.length) return 50.0;
  const totalWeight = present.reduce((sum, [, w]) => sum + w, 0);
  const score = present.reduce((sum, [v, w]) => sum + v * w, 0) / totalWeight;
  return Math.round(Math.max(0, Math.min(100, score)) * 10) / 10;
}

export function stockScoreLabel(score: number): string {
  if (score >= 82) return "A+ Honey Quality";
  if (score >= 70) return "A Quality Watchlist";
  if (score >= 55) return "B Selective Study";
  if (score >= 40) return "C Caution Zone";
  return "D Weak Honey";
}

export function scoreVerdict(score: number): string {
  if (score >= 75) return "Strong fundamentals candidate. Now valuation and business durability need deeper study.";
  if (score >= 55) return "Mixed but research-worthy. Fix the weak filters before getting excited.";
  return "Caution first. The scanner found enough weak fundamentals to slow down.";
}

/** Port of classify_business_quality (rich client-side version). */
export function classifyBusinessQuality(summary: Summary | null, filters: QualityFilter[]): Classification & { icon: string; explanation: string } {
  const s = (summary || {}) as Summary;
  const score = buildFinancifyStockScore(s, filters);
  const failed = countStatus(filters, "Fail");
  const missing = countStatus(filters, "Missing");
  const cfoPat = safeFloat(s.latest_cfo_pat, 0);
  const fcf = safeFloat(s.latest_fcf, 0);
  const roe = safeFloat(s.latest_roe, 0);
  const roce = safeFloat(s.latest_roce, 0);
  // latest_de is null (not 0) when net worth is zero/negative - the ratio is
  // undefined, not "no debt". Defaulting it to 0 here previously let a company
  // with accumulated losses exceeding paid-up capital pass the `debt < 0.8`
  // check and even qualify as a "Compounder Candidate", the best possible
  // verdict, purely because its worst balance-sheet risk read as zero.
  const debt = finiteOrNull(s.latest_de);
  const opm = safeFloat(s.latest_op_margin, 0);

  if (missing >= 5) {
    return { name: "Insufficient Data", icon: "help", explanation: "Too many numbers are missing to form a real opinion here. Better to wait for fuller data than guess.", confidence: "Low", score };
  }
  if (score >= 82 && roe >= 18 && roce >= 18 && cfoPat >= 0.9 && debt !== null && debt < 0.8) {
    return { name: "Compounder Candidate", icon: "trophy", explanation: "Returns, cash conversion and the balance sheet all hold up. A business worth understanding properly.", confidence: "High", score };
  }
  if (score >= 72 && failed <= 6) {
    return { name: "Strong Business, Selective Study", icon: "hexagon", explanation: "An attractive business, but a few filters still need checking before you'd call it settled.", confidence: missing <= 1 ? "High" : "Moderate", score };
  }
  if (fcf <= 0 || cfoPat < 0.75) {
    return { name: "Cash-Flow Concern", icon: "droplets", explanation: "Profits on paper aren't showing up as cash. Worth understanding why before going further.", confidence: "High", score };
  }
  if (debt === null || debt >= 1.2) {
    return { name: "Highly Leveraged", icon: "alert-triangle", explanation: debt === null ? "Net worth is zero or negative - that's a balance-sheet risk you can't rule out." : "Debt is doing a lot of the talking here. That's a risk, not just a detail.", confidence: "High", score };
  }
  if (score >= 60 && opm > 0) {
    return { name: "Improving or Mixed Business", icon: "trending-up", explanation: "There's something here worth researching, but the quality isn't consistent yet.", confidence: "Moderate", score };
  }
  return { name: "Weak Quality", icon: "circle-alert", explanation: "Nothing wrong with passing here. Too many quality checks came back weak to build real conviction.", confidence: "Moderate", score };
}

export interface InvestorFit {
  label: string;
  score: number;
  strengths: string[];
  concerns: string[];
}

/** Port of calculate_investor_fit. */
export function calculateInvestorFit(profile: string, filters: QualityFilter[]): InvestorFit {
  const priorities = INVESTOR_PROFILES[profile] || [];
  const relevant = (filters || []).filter((f) => priorities.includes(String(f.Filter)));
  if (!relevant.length) {
    return { label: "Insufficient data", score: 0, strengths: [], concerns: priorities.slice(0, 3) };
  }
  const passCount = relevant.filter((f) => String(f.Result).includes("Pass")).length;
  const score = Math.round((passCount / Math.max(relevant.length, 1)) * 100);
  const label = score >= 70 ? "Strong" : score >= 45 ? "Moderate" : "Weak";
  const strengths = relevant.filter((f) => String(f.Result).includes("Pass")).map((f) => String(f.Filter)).slice(0, 3);
  const concerns = relevant.filter((f) => !String(f.Result).includes("Pass")).map((f) => String(f.Filter)).slice(0, 3);
  return { label, score, strengths, concerns };
}

/** Port of build_honey_diagnostics. */
export function buildHoneyDiagnostics(filters: QualityFilter[], maxItems = 6): string[] {
  if (!filters || !filters.length) return ["Some filter values are missing, so diagnostics are limited."];
  const focus = filters.filter((f) => ["Fail", "Watch", "Missing"].includes(statusOf(f.Result)));
  if (!focus.length) {
    return ["No major filter damage found. Still compare valuation, business quality and industry risks before any decision."];
  }
  return focus.slice(0, maxItems).map((row) => {
    const filt = String(row.Filter || "Check");
    const status = statusOf(row.Result);
    const latest = String(row["Latest Value"] ?? "N/A");
    const rule = String(row.Target || "Review this parameter.");
    if (status === "Fail") return `${filt} failed. Latest value: ${latest}. Rule: ${rule}`;
    if (status === "Watch") return `${filt} is in watch zone. Latest value: ${latest}. Rule: ${rule}`;
    return `${filt} is missing. Add this input for a cleaner scan.`;
  });
}

/** Port of build_investor_actions. */
export function buildInvestorActions(score: number, summary: Summary | null): [string, string, string] {
  const signal = String(summary?.latest_signal || "").toLowerCase();
  let conservative: string, moderate: string, aggressive: string;
  if (score >= 75) {
    conservative = "Add to watchlist and study valuation margin of safety before buying.";
    moderate = "Compare with 2-3 peers and wait for sensible valuation or correction.";
    aggressive = "Deep-dive company filings, moat, management quality and growth runway.";
  } else if (score >= 55) {
    conservative = "Do not rush. Use it as a research candidate, not an instant buy.";
    moderate = "Check failed filters and decide if issues are temporary or structural.";
    aggressive = "Track quarterly improvements and build a small watch position only after valuation comfort.";
  } else {
    conservative = "Avoid fresh excitement until fundamentals improve clearly.";
    moderate = "Use the tool report as a problem checklist before deeper study.";
    aggressive = "Only study as a turnaround case; protect capital first.";
  }
  if (signal.includes("sell") || signal.includes("avoid")) {
    aggressive = "Even aggressive investors should avoid blind bottom-fishing until core numbers improve.";
  }
  return [conservative, moderate, aggressive];
}

/** Port of build_buffett_note. */
export function buildBuffettNote(score: number, summary: Summary | null): string {
  const roe = safeFloat(summary?.latest_roe, 0);
  const roce = safeFloat(summary?.latest_roce, 0);
  // null means net worth is zero/negative (undefined ratio), not "no debt" - see
  // the identical fix in classifyBusinessQuality above.
  const de = finiteOrNull(summary?.latest_de);
  const fcf = safeFloat(summary?.latest_fcf, 0);
  if (score >= 75 && roe >= 20 && roce >= 20 && de !== null && de < 0.8 && fcf > 0) {
    return "Warren Buffett would probably still ask one boring question: is this a wonderful business at a sensible price? Quality looks interesting, but valuation decides the final move.";
  }
  if (de === null || de >= 0.8) return "Buffett would likely slow down here. Debt can quietly eat the honey before shareholders taste it.";
  if (fcf <= 0) return "Buffett would likely ask where the cash is. Accounting profit without free cash flow needs careful checking.";
  if (roe < 15 || roce < 15) return "Buffett would likely question the durability of returns. A business that cannot earn strong returns may not deserve premium valuation.";
  return "Buffett would likely stay patient, read company filings, compare return ratios, and only act when quality and price both make sense.";
}

// ---------------------------------------------------------------------------
// Metric helpers
// ---------------------------------------------------------------------------
export function metricValues(metricName: string, metrics: MetricRow[], years: string[]): (number | null)[] {
  const hit = (metrics || []).find((m) => m.Metric === metricName);
  if (!hit) return [];
  return (years || []).map((y) => finiteOrNull(hit[y]));
}

export function latestMetric(metricName: string, metrics: MetricRow[], years: string[]): number | null {
  const vals = metricValues(metricName, metrics, years);
  for (let i = vals.length - 1; i >= 0; i--) {
    if (vals[i] !== null) return vals[i];
  }
  return null;
}

/** Trailing CAGR between the series' latest value and the value exactly
 * `yearsBack` columns earlier (same formula and positional convention as
 * eps_cagr_pct/buildRevenueForecast elsewhere in this file: (end/start)^
 * (1/years) - 1, using the raw year positions rather than compacting out
 * gaps). Returns null if that many years of history aren't present, either
 * position is missing, or the starting value isn't positive (a CAGR from
 * zero or a negative base is undefined). */
function trailingCagrPct(values: (number | null)[], yearsBack: number): number | null {
  if (yearsBack < 1 || values.length <= yearsBack) return null;
  const end = values[values.length - 1];
  const start = values[values.length - 1 - yearsBack];
  if (start === null || start <= 0 || end === null) return null;
  return (Math.pow(end / start, 1 / yearsBack) - 1) * 100;
}

export interface TrailingCagrRow {
  label: string;
  cagr3y: number | null;
  cagr5y: number | null;
}

/** 3-year and 5-year trailing CAGR for a fixed set of headline metrics
 * (PAT, Revenue, EPS, ROE, ROA, ROCE), for the small-card strip under the
 * Financial Trends 5-year table. 5-year CAGR spans the full column range
 * (years.length - 1 growth periods); 3-year CAGR spans the last 3 growth
 * periods - both trailing from the latest column. */
export function buildTrailingCagrTable(metrics: MetricRow[], years: string[]): TrailingCagrRow[] {
  const specs: { label: string; metric: string }[] = [
    { label: "Profit After Tax", metric: "Profit After Tax" },
    { label: "Revenue", metric: "Revenue" },
    { label: "EPS", metric: "Earnings Per Share" },
    { label: "ROE", metric: "Return on Equity" },
    { label: "ROA", metric: "Return on Assets" },
    { label: "ROCE", metric: "Return on Capital Employed" },
  ];
  return specs.map(({ label, metric }) => {
    const values = metricValues(metric, metrics, years);
    return {
      label,
      cagr3y: trailingCagrPct(values, 3),
      cagr5y: trailingCagrPct(values, years.length - 1),
    };
  });
}

/** Port of _trend_label. */
export function trendLabel(values: (number | null)[], higherIsBetter = true): string {
  const clean = values.filter((v): v is number => v !== null);
  if (clean.length < 3) return "Insufficient data";
  const change = clean[clean.length - 1] - clean[0];
  // Only guards against a true near-zero starting value blowing up the
  // ratio (e.g. 0.001 -> 0.01 reading as a 900% "change"). A flat 1.0 floor
  // instead understated real relative movement for metrics whose natural
  // scale is under 1 (e.g. Inventory Turnover moving 0.3x -> 0.6x is a
  // genuine 100% change, not "Stable").
  const scale = Math.max(Math.abs(clean[0]), 0.01);
  if (Math.abs(change) / scale < 0.05) return "Stable";
  const improved = higherIsBetter ? change > 0 : change < 0;
  return improved ? "Improving" : "Deteriorating";
}

export function metricHigherIsBetter(metricName: string): boolean {
  return !LOWER_IS_BETTER.has(metricName);
}

// ---------------------------------------------------------------------------
// Research questions (port of generate_research_questions)
// ---------------------------------------------------------------------------
export function generateResearchQuestions(filters: QualityFilter[]): ResearchQuestion[] {
  const questions: ResearchQuestion[] = [];
  for (const row of filters || []) {
    const status = statusOf(row.Result);
    if (status !== "Fail" && status !== "Watch") continue;
    const name = String(row.Filter);
    const meta = FILTER_EXPLANATIONS[name];
    if (!meta) continue;
    const category = ["Inventory", "Debtor", "Payable", "Cash Conversion Cycle", "Working Capital"].some((x) => name.includes(x))
      ? "Working capital"
      : name.includes("Cash") || name.includes("Free Cash")
        ? "Cash flow"
        : ["Capital Expenditure", "Reserves", "Equity", "Goodwill"].some((x) => name.includes(x))
          ? "Capital allocation"
          : "Profitability and returns";
    questions.push({
      Priority: status === "Fail" ? "High" : "Medium",
      Category: category,
      Question: `Why does ${name.toLowerCase()} not meet the preferred rule?`,
      "Triggered by": `${row["Latest Value"] ?? "N/A"} versus ${row.Target || "preferred level"}`,
      "Where to look": meta.steps.slice(0, 2).join("; "),
    });
  }
  questions.push(
    { Priority: "Medium", Category: "General due diligence", Question: "Are there material contingent liabilities or regulatory disputes?", "Triggered by": "General annual-report review", "Where to look": "Contingent-liability note and legal proceedings" },
    { Priority: "Medium", Category: "General due diligence", Question: "Is customer or segment concentration increasing?", "Triggered by": "General annual-report review", "Where to look": "Segment note, customer concentration and management discussion" },
    { Priority: "Low", Category: "General due diligence", Question: "Do management incentives align with long-term per-share value creation?", "Triggered by": "General annual-report review", "Where to look": "Remuneration report and share-based payment note" },
  );
  return questions;
}

// ---------------------------------------------------------------------------
// Data reconstruction from input_table
// ---------------------------------------------------------------------------
export function financialsFromInputTable(inputTable: Record<string, any>[], years: string[]): Record<string, (number | null)[]> {
  const fin: Record<string, (number | null)[]> = {};
  for (const row of inputTable || []) {
    const field = String(row.Field ?? row.field ?? "").trim();
    if (!field) continue;
    fin[field] = (years || YEAR_LABELS).map((y) => finiteOrNull(row[y]));
  }
  return fin;
}

export function emptyFinancials(): Record<string, (number | null)[]> {
  const fin: Record<string, (number | null)[]> = {};
  for (const field of MANUAL_FIELDS) fin[field] = [0, 0, 0, 0, 0];
  return fin;
}

export function latestOf(data: ScanData | null, key: string): number {
  const arr = data?.financials?.[key];
  if (!Array.isArray(arr) || !arr.length) return 0;
  for (let i = arr.length - 1; i >= 0; i--) {
    const v = finiteOrNull(arr[i]);
    if (v !== null) return v;
  }
  return 0;
}

export function withLatestValue(fin: Record<string, (number | null)[]>, key: string, value: number): void {
  const arr = Array.isArray(fin[key]) && fin[key].length ? [...fin[key]] : [0, 0, 0, 0, 0];
  arr[arr.length - 1] = value;
  fin[key] = arr;
}

/** Share text port. */
export function buildShareText(company: string, ticker: string, score: number, label: string, passed: number, failed: number): string {
  return `Tenet Result by Financify\n\nCompany: ${company}\nTicker: ${ticker || "N/A"}\nFinancify Score: ${Math.round(score)}/100\nQuality Label: ${label}\nPassed Filters: ${passed}\nFailed Filters: ${failed}\n\nQuality first, valuation second.\n\nTry Tenet: https://financify.blog/financifytools`;
}

export function filterCounts(filters: QualityFilter[]) {
  return {
    passed: countStatus(filters, "Pass"),
    failed: countStatus(filters, "Fail"),
    watch: countStatus(filters, "Watch"),
    missing: countStatus(filters, "Missing"),
  };
}

// ---------------------------------------------------------------------------
// SWOT Analysis (replaces the old Honey Flags section on the Overview tab)
// ---------------------------------------------------------------------------
export interface SwotItem {
  title: string;
  detail: string;
}

export interface SwotAnalysis {
  strengths: SwotItem[];
  weaknesses: SwotItem[];
  opportunities: SwotItem[];
  threats: SwotItem[];
  swotScore: number;
  swotLabel: string;
}

/** 0-100 read of the net SWOT balance: strengths/opportunities push it up,
 * weaknesses/threats pull it down. Independent of the Financify Score - this
 * only summarises the four quadrants above, not the underlying financials. */
function computeSwotScore(strengthCount: number, weaknessCount: number, opportunityCount: number, threatCount: number): number {
  const raw = strengthCount * 10 - weaknessCount * 10 + opportunityCount * 5 - threatCount * 5;
  const span = 4 * 10 + 4 * 5; // matches the 4-item display cap on each quadrant
  return Math.round(Math.max(0, Math.min(100, 50 + (raw / span) * 50)));
}

function swotScoreLabel(score: number): string {
  if (score >= 70) return "SWOT Positive";
  if (score >= 45) return "SWOT Balanced";
  return "SWOT Negative";
}

/** Derives a Strengths/Weaknesses/Opportunities/Threats read from the same
 * quality filters and summary flags the scanner already computes - no new
 * backend data required. */
export function buildSwotAnalysis(summary: Summary | null, filters: QualityFilter[]): SwotAnalysis {
  const s = (summary || {}) as Summary;
  const passRows = (filters || []).filter((f) => statusOf(f.Result) === "Pass");
  const failRows = (filters || []).filter((f) => statusOf(f.Result) === "Fail");
  const watchRows = (filters || []).filter((f) => statusOf(f.Result) === "Watch");

  const strengths: SwotItem[] = passRows.slice(0, 4).map((f) => ({
    title: String(f.Filter),
    detail: `Latest value ${f["Latest Value"] ?? "N/A"} meets the preferred rule (${f.Target || "target"}).`,
  }));
  if (!strengths.length) {
    strengths.push({ title: "No confirmed strengths yet", detail: "None of the quality filters have cleanly passed on the latest data available." });
  }

  const weaknesses: SwotItem[] = failRows.slice(0, 4).map((f) => ({
    title: String(f.Filter),
    detail: `Latest value ${f["Latest Value"] ?? "N/A"} misses the preferred rule (${f.Target || "target"}).`,
  }));
  if (!weaknesses.length) {
    weaknesses.push({ title: "No hard filter failures", detail: "No quality filter has clearly failed on the latest data available." });
  }

  const opportunities: SwotItem[] = [];
  if (s.explosive) opportunities.push({ title: "Explosive growth pattern", detail: "High margins combined with high ROE and ROCE - a rare, research-worthy combination." });
  if (s.turnaround) opportunities.push({ title: "Early turnaround signals", detail: "Revenue growth, cash flow and efficiency are all improving together." });
  if (s.score_trend === "▲ Improving") opportunities.push({ title: "Improving Financify Score", detail: "The overall quality score has moved up versus the prior year." });
  for (const f of watchRows.slice(0, 3)) {
    opportunities.push({ title: `${String(f.Filter)} is close to the target`, detail: `Currently in the watch zone (${f["Latest Value"] ?? "N/A"}) - a small improvement could flip this into a strength.` });
  }
  const realOpportunityCount = opportunities.length;
  if (!opportunities.length) {
    opportunities.push({ title: "No standout opportunity flagged", detail: "Nothing in the current data suggests an emerging positive inflection." });
  }

  const threats: SwotItem[] = [];
  const debt = finiteOrNull(s.latest_de);
  if (debt === null || debt >= 1.2) {
    threats.push({ title: "Balance-sheet leverage", detail: debt === null ? "Net worth is zero or negative, so leverage risk cannot be ruled out." : "Outside liabilities are high relative to net worth." });
  }
  if (safeFloat(s.latest_fcf, 0) <= 0 || safeFloat(s.latest_cfo_pat, 0) < 0.75) {
    threats.push({ title: "Cash conversion risk", detail: "Reported profit is not converting strongly into free cash flow." });
  }
  const missingCount = (filters || []).filter((f) => statusOf(f.Result) === "Missing").length;
  if (missingCount >= 5) {
    threats.push({ title: "Data confidence is low", detail: "Several key inputs are unavailable, so any verdict here carries extra uncertainty." });
  }
  for (const f of watchRows.slice(0, 2)) {
    threats.push({ title: `${String(f.Filter)} needs monitoring`, detail: `Currently in the watch zone (${f["Latest Value"] ?? "N/A"}) - a further slip would turn this into a failure.` });
  }
  const realThreatCount = threats.length;
  if (!threats.length) {
    threats.push({ title: "No major threat flagged", detail: "No balance-sheet, cash-flow or data-confidence red flag stands out currently." });
  }

  // Use counts from before the "nothing found" placeholder text was pushed -
  // otherwise a company with zero real opportunities/threats would score as
  // if it had exactly one of each, silently skewing the score.
  const swotScore = computeSwotScore(
    Math.min(passRows.length, 4),
    Math.min(failRows.length, 4),
    Math.min(realOpportunityCount, 4),
    Math.min(realThreatCount, 4)
  );

  return {
    strengths,
    weaknesses,
    opportunities: opportunities.slice(0, 4),
    threats: threats.slice(0, 4),
    swotScore,
    swotLabel: swotScoreLabel(swotScore),
  };
}

// ---------------------------------------------------------------------------
// "What Would Warren Do" — replaces Quality Interpretation on the Overview tab
// ---------------------------------------------------------------------------
export interface WarrenVerdict {
  available: boolean;
  revenueYieldPct: number | null;
  thresholdPct: number;
  verdict: "Clears Buffett's Bar" | "Avoid";
  explanation: string;
}

/** Buffett's "don't buy a single share of a business you wouldn't buy
 * outright" test: if owning the whole company (Total Revenue / Market Cap)
 * would out-earn a safe AAA corporate bond, it clears the bar. Clearing this
 * one test is not a buy signal by itself - Buffett would still weigh
 * valuation, business quality, and moat before buying as much as he can. */
export function buildWarrenVerdict(summary: Summary | null): WarrenVerdict {
  const s = (summary || {}) as Summary;
  const yieldPct = finiteOrNull(s.warren_revenue_yield_pct);
  const threshold = safeFloat(s.warren_bond_yield_threshold_pct, 12);
  if (yieldPct === null) {
    return {
      available: false,
      revenueYieldPct: null,
      thresholdPct: threshold,
      verdict: "Avoid",
      explanation: "Market price / market cap data isn't available for this scan, so the whole-business return can't be calculated yet.",
    };
  }
  const clears = yieldPct >= threshold;
  return {
    available: true,
    revenueYieldPct: yieldPct,
    thresholdPct: threshold,
    verdict: clears ? "Clears Buffett's Bar" : "Avoid",
    explanation: clears
      ? `If you owned this entire business, Total Revenue / Market Cap works out to ${yieldPct.toFixed(1)}% - above the ${threshold}% a safe AAA corporate bond pays. Buffett's whole-business test clears this bar, but he'd still weigh valuation, business quality, and moat before buying as much as he can - this metric alone isn't a green light.`
      : `If you owned this entire business, Total Revenue / Market Cap works out to ${yieldPct.toFixed(1)}% - below the ${threshold}% a safe AAA corporate bond pays. Buffett's whole-business test says a bond is the safer return here.`,
  };
}

// ---------------------------------------------------------------------------
// Valuation tab (Standard / Starter / Pro only)
// ---------------------------------------------------------------------------
export type ValuationVerdict = "Undervalued" | "Fair Valued" | "Overvalued" | "Insufficient Data";

export interface ValuationMetric {
  key: string;
  label: string;
  value: number | null;
  unit: string;
  benchmark: number | null;
  benchmarkLabel: string;
  verdict: ValuationVerdict;
  explanation: string;
  // Why benchmark can be null - shown in place of a bare "N/A" so a company
  // with a genuinely short or gappy history (recently listed, an erratic
  // earnings record) reads as "here's why", not "something broke".
  insufficientReason: string;
}

export interface GrahamNumberAnalysis {
  value: number | null;
  currentPrice: number | null;
  verdict: ValuationVerdict;
  explanation: string;
}

export interface IntrinsicValueAnalysis {
  value: number | null;
  currentPrice: number | null;
  verdict: ValuationVerdict;
  explanation: string;
}

// Deliberately its own verdict type, not ValuationVerdict - Dividend Yield
// isn't an over/undervaluation signal (there's no "own history" trend or
// fair-value estimate behind it, just Screener's own latest reported
// figure), so "Overvalued"/"Undervalued" would be a category error here.
export type DividendYieldVerdict = "Attractive Yield" | "Modest Yield" | "No Dividend" | "Insufficient Data";

export interface DividendYieldAnalysis {
  value: number | null;
  verdict: DividendYieldVerdict;
  explanation: string;
}

export interface ValuationAnalysis {
  overallVerdict: ValuationVerdict;
  financifyValuationScore: number;
  metrics: ValuationMetric[];
  // Kept separate from `metrics` deliberately: the other four are each
  // compared against the company's OWN historical median, and feed the
  // blended Financify Valuation Score. Graham Number and Intrinsic Value are
  // both one-shot fair-value-per-share estimates compared against today's
  // price, not a trend vs. their own history - folding either into the same
  // average would silently change what that score has always meant.
  grahamNumber: GrahamNumberAnalysis;
  // Graham's growth-formula estimate (EPS x (8.5 + 2g) x 4.4 / Y) - unlike
  // Graham Number, this one prices in the company's own trailing EPS growth,
  // so it can differ meaningfully for high or negative growth businesses.
  intrinsicValue: IntrinsicValueAnalysis;
  // Screener's own latest reported figure - no historical series exists to
  // compare it against (unlike PE/PEG/Earnings Yield/EV-EBITDA above), so
  // it gets its own DividendYieldVerdict rather than being squeezed into
  // ValuationVerdict or averaged into the Financify Valuation Score.
  dividendYield: DividendYieldAnalysis;
}

function verdictLowerIsCheaper(value: number | null, benchmark: number | null): ValuationVerdict {
  // A zero or negative multiple (loss-making year, missing data) is
  // undefined, not "cheap" - never let it read as below-benchmark.
  if (value === null || value <= 0 || benchmark === null || benchmark <= 0) return "Insufficient Data";
  if (value < benchmark * 0.95) return "Undervalued";
  if (value > benchmark * 1.05) return "Overvalued";
  return "Fair Valued";
}

function verdictHigherIsCheaper(value: number | null, benchmark: number | null): ValuationVerdict {
  if (value === null || benchmark === null || benchmark <= 0) return "Insufficient Data";
  if (value > benchmark * 1.05) return "Undervalued";
  if (value < benchmark * 0.95) return "Overvalued";
  return "Fair Valued";
}

/** Signed fractional deviation of value from its own historical median,
 * sign-flipped so positive always means "cheaper than usual". Port of
 * honey_core.py's _score_deviation - used only for the continuous
 * financifyValuationScore below, not the per-metric verdict cards (those
 * stay on the existing Undervalued/Fair/Overvalued classification). */
function scoreDeviation(value: number | null, benchmark: number | null, lowerIsCheaper: boolean): number | null {
  if (value === null || benchmark === null || value <= 0 || benchmark <= 0) return null;
  const raw = (value - benchmark) / benchmark;
  return lowerIsCheaper ? -raw : raw;
}

/** Builds the four valuation-metric cards + a combined Financify Valuation
 * Score. Each metric is compared only against this company's OWN historical
 * median for that metric (see `valuation_formula` in the summary) - never an
 * industry, sector, or flat market benchmark. */
export function buildValuationAnalysis(summary: Summary | null): ValuationAnalysis {
  const s = (summary || {}) as Summary;
  const pe = finiteOrNull(s.latest_pe_ratio);
  const medianPe = finiteOrNull(s.historical_median_pe);
  const peg = finiteOrNull(s.latest_peg_ratio);
  const medianPeg = finiteOrNull(s.historical_median_peg);
  const earningsYield = finiteOrNull(s.latest_earnings_yield_pct);
  const medianEarningsYield = finiteOrNull(s.historical_median_earnings_yield_pct);
  const evEbitda = finiteOrNull(s.latest_ev_ebitda);
  const medianEvEbitda = finiteOrNull(s.historical_median_ev_ebitda);
  // Each metric can have a different number of contributing years (PEG
  // always excludes the oldest year and any non-positive-growth year;
  // Earnings Yield includes loss-making years PE/PEG must exclude;
  // EV/EBITDA doesn't depend on EPS at all) - label each card with its own
  // real sample size, not a single shared count borrowed from PE.
  const yearsLabel = (key: string) => {
    const n = Number((s as any)[key]) || 0;
    return n > 0 ? `${n}-Year Historical Median` : "Historical Median (insufficient data)";
  };

  const metrics: ValuationMetric[] = [
    {
      key: "pe",
      label: "PE Ratio",
      value: pe,
      unit: "x",
      benchmark: medianPe,
      benchmarkLabel: yearsLabel("historical_pe_years_used"),
      verdict: verdictLowerIsCheaper(pe, medianPe),
      explanation: "Price / Earnings per Share versus this company's own historical median PE. Lower than its own median suggests the market is pricing it more cheaply than its own history.",
      insufficientReason: "No year-end stock price could be resolved - usually means this company was listed too recently for enough trading history.",
    },
    {
      key: "peg",
      label: "PEG Ratio",
      value: peg,
      unit: "x",
      benchmark: medianPeg,
      benchmarkLabel: yearsLabel("historical_peg_years_used"),
      verdict: verdictLowerIsCheaper(peg, medianPeg),
      explanation: "PE Ratio divided by trailing EPS growth, versus this company's own historical median PEG.",
      insufficientReason: "Not enough years with both a resolvable price and positive EPS growth - PEG excludes any year EPS fell, on top of needing the price history PE needs.",
    },
    {
      key: "earnings_yield",
      label: "Earnings Yield",
      value: earningsYield,
      unit: "%",
      benchmark: medianEarningsYield,
      benchmarkLabel: yearsLabel("historical_earnings_yield_years_used"),
      verdict: verdictHigherIsCheaper(earningsYield, medianEarningsYield),
      explanation: "Net Profit / Market Cap - the inverse of PE. A higher earnings yield than this company's own historical median means more profit per rupee of price paid than usual.",
      insufficientReason: "No year-end stock price could be resolved - usually means this company was listed too recently for enough trading history.",
    },
    {
      key: "ev_ebitda",
      label: "EV / EBITDA",
      value: evEbitda,
      unit: "x",
      benchmark: medianEvEbitda,
      benchmarkLabel: yearsLabel("historical_ev_ebitda_years_used"),
      verdict: verdictLowerIsCheaper(evEbitda, medianEvEbitda),
      explanation: "Enterprise Value (Market Cap + Total Debt) / EBITDA, versus this company's own historical median - a capital-structure-neutral way to compare today's valuation to its own history.",
      insufficientReason: "No year-end stock price could be resolved - usually means this company was listed too recently for enough trading history.",
    },
  ];

  // financifyValuationScore comes from the continuous formula (averaged
  // signed deviation from each metric's own historical median, magnitude-
  // preserving) rather than averaging the metrics' +1/0/-1 verdict buckets -
  // the per-metric verdicts above stay discrete since those remain useful as
  // individual card labels, only the aggregate score changed.
  const deviations = [
    scoreDeviation(pe, medianPe, true),
    scoreDeviation(peg, medianPeg, true),
    scoreDeviation(earningsYield, medianEarningsYield, false),
    scoreDeviation(evEbitda, medianEvEbitda, true),
  ]
    .filter((d): d is number => d !== null)
    .map((d) => Math.max(-1, Math.min(1, d)));
  const financifyValuationScore = deviations.length
    ? Math.round(Math.max(0, Math.min(100, 50 + (deviations.reduce((a, b) => a + b, 0) / deviations.length) * 50)) * 10) / 10
    : 50;

  const valid = metrics.filter((m) => m.verdict !== "Insufficient Data");
  let overallVerdict: ValuationVerdict = "Insufficient Data";
  if (valid.length) {
    const tally: Record<string, number> = {};
    for (const m of valid) tally[m.verdict] = (tally[m.verdict] || 0) + 1;
    const entries = Object.entries(tally).sort((a, b) => b[1] - a[1]);
    overallVerdict = (entries[0][1] > (entries[1]?.[1] ?? 0) ? entries[0][0] : "Fair Valued") as ValuationVerdict;
  }

  const grahamValue = finiteOrNull(s.latest_graham_number);
  const currentPrice = finiteOrNull(s.latest_current_price);
  const grahamNumber: GrahamNumberAnalysis = {
    value: grahamValue,
    currentPrice,
    // Reuses the existing "lower value is cheaper" comparison: here the
    // "value" being judged is the current price, and the "benchmark" it's
    // judged against is the Graham Number fair-value estimate - a price
    // meaningfully below that estimate reads as Undervalued.
    verdict: verdictLowerIsCheaper(currentPrice, grahamValue),
    explanation: "Benjamin Graham's classic fair-value-per-share estimate: sqrt(22.5 x EPS x Book Value per share). A current price below this suggests a margin of safety versus that estimate; it says nothing about business quality or growth on its own.",
  };

  const intrinsicVal = finiteOrNull(s.latest_intrinsic_value_graham);
  const intrinsicValue: IntrinsicValueAnalysis = {
    value: intrinsicVal,
    currentPrice,
    verdict: verdictLowerIsCheaper(currentPrice, intrinsicVal),
    explanation: "Benjamin Graham's growth-formula fair-value estimate: EPS x (8.5 + 2 x 5-year EPS growth%) x 4.4 / assumed AAA bond yield. Unlike the Graham Number above, this one prices in the company's own earnings growth - it can run far higher for fast-growing companies and lower (or undefined) for shrinking ones.",
  };

  const dividendYieldValue = finiteOrNull(s.latest_dividend_yield_pct);
  let dividendYieldVerdict: DividendYieldVerdict;
  if (dividendYieldValue === null) dividendYieldVerdict = "Insufficient Data";
  else if (dividendYieldValue <= 0) dividendYieldVerdict = "No Dividend";
  else if (dividendYieldValue >= 1.5) dividendYieldVerdict = "Attractive Yield";
  else dividendYieldVerdict = "Modest Yield";
  const dividendYield: DividendYieldAnalysis = {
    value: dividendYieldValue,
    verdict: dividendYieldVerdict,
    explanation: dividendYieldVerdict === "No Dividend"
      ? "This company currently pays no dividend - common (and not itself a red flag) for businesses reinvesting the bulk of their profit for growth rather than distributing it."
      : "Annual dividend per share as a percentage of the current share price - the direct cash return paid to shareholders, separate from any price gain or loss. Moves daily as the price changes, same as PE Ratio.",
  };

  return { overallVerdict, financifyValuationScore, metrics, grahamNumber, intrinsicValue, dividendYield };
}

// ---------------------------------------------------------------------------
// Scan Tracker categories - non-advisory descriptive labels derived from the
// two scores above. Never phrased as an action (no Buy/Sell/Hold/Avoid) -
// each label states a fact about the business or its price versus its own
// history. Port of honey_core.py's classify_quality_tier/classify_valuation_tier/
// combined_scan_category - keep these two files' tier tables in sync if either
// changes. Quality Tier cutoffs are calibrated from the real distribution of
// quality_score across research/data/panel.csv (~2,000+ real NSE
// company-years, see research/panel_percentiles.py) - not guessed round numbers.
// ---------------------------------------------------------------------------

export interface ScoreTier {
  tier: string;
  label: string;
}

// Real quintile boundaries (p80/p60/p40/p20) of quality_score across 2,163
// real NSE company-years (Nifty 500 universe, 2015-2026, see
// research/panel_percentiles.py output) - not round numbers. Real
// distribution: min=0.0 max=94.4 mean=58.0 median=61.1, p90=76.3 - a
// round-number cutoff like 80 would have put fewer than 10% of real
// companies in the top tier. Keep in sync with honey_core.py's QUALITY_TIERS.
const QUALITY_TIERS: [number, string, string][] = [
  [71.5, "A", "Compounder-Quality Business"],
  [64.5, "B", "Strong Business"],
  [57.2, "C", "Steady Business"],
  [46.3, "D", "Inconsistent Business"],
  [0, "E", "Fragile Business"],
];

const VALUATION_TIERS: [number, string, string][] = [
  [80, "Deep Discount", "Historically Cheap"],
  [65, "Below Norm", "Value Zone"],
  [35, "In Line", "Fairly Priced"],
  [20, "Above Norm", "Premium Zone"],
  [0, "Rich Premium", "Historically Expensive"],
];

const COMBINED_CATEGORY_GRID: Record<string, string> = {
  "High|Cheap": "Compounder-Quality Business in the Value Zone",
  "High|Fair": "Compounder-Quality Business at Fair Value",
  "High|Expensive": "Compounder-Quality Business at a Premium",
  "Medium|Cheap": "Steady Business, Historically Cheap",
  "Medium|Fair": "Steady Business, Fairly Priced",
  "Medium|Expensive": "Steady Business at a Premium",
  "Low|Cheap": "Fragile Business, Historically Cheap",
  "Low|Fair": "Fragile Business, Fairly Priced",
  "Low|Expensive": "Fragile Business at a Premium",
};

export function classifyQualityTier(score: number | null): ScoreTier {
  if (score === null) return { tier: "Unknown", label: "Insufficient Data" };
  for (const [threshold, tier, label] of QUALITY_TIERS) {
    if (score >= threshold) return { tier, label };
  }
  return { tier: "E", label: "Fragile Business" };
}

export function classifyValuationTier(score: number | null): ScoreTier {
  if (score === null) return { tier: "Unknown", label: "Insufficient Data" };
  for (const [threshold, tier, label] of VALUATION_TIERS) {
    if (score >= threshold) return { tier, label };
  }
  return { tier: "Rich Premium", label: "Historically Expensive" };
}

/** Collapses the 5-tier Quality/Valuation scores into a 3x3 grid (High/
 * Medium/Low quality x Cheap/Fair/Expensive) for a single primary Scan
 * Tracker badge - the full 5x5 combination (25 cells) is too granular to be
 * a useful single category. The two 5-tier scores stay available separately
 * (classifyQualityTier/classifyValuationTier) for anyone who wants
 * finer-grained filtering. */
export function combinedScanCategory(qualityScore: number | null, valuationScore: number | null): string {
  if (qualityScore === null || valuationScore === null) return "Insufficient Data";
  // High = Quality Tier A+B, Medium = Tier C, Low = Tier D+E (real quintile
  // boundaries from QUALITY_TIERS, not round numbers).
  const qualityBucket = qualityScore >= 64.5 ? "High" : qualityScore >= 57.2 ? "Medium" : "Low";
  const valuationBucket = valuationScore >= 65 ? "Cheap" : valuationScore >= 35 ? "Fair" : "Expensive";
  return COMBINED_CATEGORY_GRID[`${qualityBucket}|${valuationBucket}`];
}

// ---------------------------------------------------------------------------
// Next-year forecast (Pro only) — simple Buffettology-style projection
// ---------------------------------------------------------------------------
export interface ForecastScenario {
  epsGrowthPct: number;
  projectedEps: number;
  projectedPrice: number;
  peUsed: number;
}

export interface NextYearForecast {
  available: boolean;
  latestEps: number | null;
  epsCagrPct: number | null;
  currentPrice: number | null;
  peSource: string | null;
  base: ForecastScenario | null;
  bull: ForecastScenario | null;
  bear: ForecastScenario | null;
  explanation: string;
}

function projectScenario(latestEps: number, growthPct: number, peUsed: number): ForecastScenario {
  const projectedEps = latestEps * (1 + growthPct / 100);
  return { epsGrowthPct: growthPct, projectedEps, projectedPrice: projectedEps * peUsed, peUsed };
}

/** Simple-mathematics projection in the spirit of "New Buffettology": next
 * year's EPS = latest EPS grown at the company's own 5-year CAGR - the same
 * growth rate for all three cases, so Bull/Base/Bear isolate the effect of
 * the PE multiple assumption alone rather than mixing in a second, separate
 * growth-rate assumption. Base/Bull/Bear each use their own real PE
 * multiple - the average, highest and lowest PE actually seen across up to
 * 10 fiscal years with a resolvable NSE/BSE Bhavcopy price when that wider
 * dataset resolved, otherwise the 5 years the rest of the app tracks
 * (falling back further to today's current PE for all three when no
 * historical price data is available at all). latestEps is read via
 * pickChartYears (not the bare 5-year eps_series) so this always starts
 * from the exact same number the forecast chart's last real EPS point
 * shows - otherwise the dashed projected segment could visually start from
 * a different value than the one the growth math actually used. */
export function buildNextYearForecast(summary: Summary | null): NextYearForecast {
  const s = (summary || {}) as Summary;
  const { epsSeries } = pickChartYears(s);
  const latestEps = epsSeries.length ? finiteOrNull(epsSeries[epsSeries.length - 1]) : null;
  const epsCagrPct = finiteOrNull(s.eps_cagr_pct);
  const currentPrice = finiteOrNull(s.latest_current_price);
  const bullPe = finiteOrNull(s.forecast_bull_pe);
  const basePe = finiteOrNull(s.forecast_base_pe);
  const bearPe = finiteOrNull(s.forecast_bear_pe);
  const peSource = (s.historical_pe_source as string) || null;

  if (latestEps === null || latestEps <= 0 || epsCagrPct === null || basePe === null || basePe <= 0) {
    return {
      available: false,
      latestEps,
      epsCagrPct,
      currentPrice,
      peSource,
      base: null,
      bull: null,
      bear: null,
      explanation: "Not enough EPS history or market price data is available for this scan to project next year's numbers.",
    };
  }

  const base = projectScenario(latestEps, epsCagrPct, basePe);
  const bull = projectScenario(latestEps, epsCagrPct, bullPe && bullPe > 0 ? bullPe : basePe);
  const bear = projectScenario(latestEps, epsCagrPct, bearPe && bearPe > 0 ? bearPe : basePe);

  return {
    available: true,
    latestEps,
    epsCagrPct,
    currentPrice,
    peSource,
    base,
    bull,
    bear,
    explanation: `EPS growth is the same in all three cases - only the PE multiple assumed changes. Base ${basePe.toFixed(1)}x, Bull ${(bullPe && bullPe > 0 ? bullPe : basePe).toFixed(1)}x, Bear ${(bearPe && bearPe > 0 ? bearPe : basePe).toFixed(1)}x.`,
  };
}

// ---------------------------------------------------------------------------
// Combined EPS / Revenue / Stock Price chart data — real history (5 years,
// or up to 10 when the wider valuation-history dataset resolved) plus the
// next-year projection, for one interactive chart instead of three
// separate static cards.
// ---------------------------------------------------------------------------
export interface ForecastChartRow {
  year: string;
  eps: number | null;
  revenue: number | null;
  price: number | null;
  projected: boolean;
  priceBull?: number | null;
  priceBase?: number | null;
  priceBear?: number | null;
}

/** Prefers the up-to-10-year "wide" series (years_wide/*_series_wide from
 * honey_core.py, built from market["valuation_history"]) over the 5-year
 * fields the rest of the app uses, whenever the wide dataset resolved AT
 * LEAST as many years as the standard 5-year one - more candidate years
 * means more chances an NSE Bhavcopy price was resolvable for a given
 * fiscal year-end, and the historical median/PE/valuation calcs already
 * prefer it the same way. The length check matters: the wide dataset is
 * built from a single already-parsed page's own year detection, while the
 * 5-year one pools every parsed page (consolidated + standalone) - so it's
 * possible, if unlikely, for "wide" to actually resolve fewer years than
 * the standard path on some scans. Falls back to the standard 5-year
 * years/eps/revenue/price fields whenever the wide dataset isn't available
 * or would show less than the 5-year path already does. Used by
 * buildNextYearForecast/buildRevenueForecast/buildForecastChartSeries so
 * the chart's last real point and the forecast math starting from "the
 * latest EPS/revenue" always agree on the same number. */
function pickChartYears(s: Summary): { years: string[]; epsSeries: any[]; revenueSeries: any[]; priceSeries: any[] } {
  const yearsWide: any[] = Array.isArray(s.years_wide) ? s.years_wide : [];
  const yearsNarrow: any[] = Array.isArray(s.years) ? s.years : [];
  if (yearsWide.length > 0 && yearsWide.length >= yearsNarrow.length) {
    return {
      years: yearsWide as string[],
      epsSeries: Array.isArray(s.eps_series_wide) ? s.eps_series_wide : [],
      revenueSeries: Array.isArray(s.revenue_series_wide) ? s.revenue_series_wide : [],
      priceSeries: Array.isArray(s.historical_price_series_wide) ? s.historical_price_series_wide : [],
    };
  }
  return {
    years: yearsNarrow,
    epsSeries: Array.isArray(s.eps_series) ? s.eps_series : [],
    revenueSeries: Array.isArray(s.revenue_series) ? s.revenue_series : [],
    // Real NSE Bhavcopy closing prices for each fiscal year-end - any year
    // whose price couldn't be resolved stays null rather than estimated (see
    // historical_pe_from_bhavcopy in the backend).
    priceSeries: Array.isArray(s.historical_price_series) ? s.historical_price_series : [],
  };
}

/** Next-year Revenue projection, using Revenue's OWN growth trend - not
 * EPS's growth rate. Scaling revenue by the EPS growth rate (the old
 * approach) conflates two different things: EPS growth reflects margin
 * changes, buybacks/dilution, and leverage on top of whatever sales growth
 * actually happened, so borrowing it to project revenue can produce a
 * badly distorted number whenever those effects are large (e.g. a company
 * with flat sales but expanding margins would show implausibly fast
 * "revenue growth" under the old method). Revenue and EPS are now forecast
 * independently, each from its own historical trend - the same CAGR
 * formula honey_core.py uses for eps_cagr_pct, applied to revenue_series
 * instead. Returns null if revenue history is missing/non-positive or the
 * EPS forecast isn't available (shown alongside it, so the same
 * "not enough history" condition should gate both). */
export function buildRevenueForecast(summary: Summary | null, forecast: NextYearForecast): { growthPct: number; projectedRevenue: number; yearsSpan: number } | null {
  const s = (summary || {}) as Summary;
  const { revenueSeries } = pickChartYears(s);
  if (revenueSeries.length < 2 || !forecast.available) return null;
  const firstRevenue = finiteOrNull(revenueSeries[0]);
  const latestRevenue = finiteOrNull(revenueSeries[revenueSeries.length - 1]);
  if (firstRevenue === null || firstRevenue <= 0 || latestRevenue === null || latestRevenue <= 0) return null;
  const yearsSpan = revenueSeries.length - 1;
  const growthPct = (Math.pow(latestRevenue / firstRevenue, 1 / yearsSpan) - 1) * 100;
  // yearsSpan exposed so the UI can label this correctly - unlike EPS's
  // growth rate (always a fixed 5-year eps_cagr_pct from the backend),
  // Revenue's own CAGR here spans however many years pickChartYears
  // resolved (up to 9, when the wide valuation-history dataset is
  // available), so a hardcoded "5-yr" label would be wrong.
  return { growthPct, projectedRevenue: latestRevenue * (1 + growthPct / 100), yearsSpan };
}

export function buildForecastChartSeries(summary: Summary | null, forecast: NextYearForecast): ForecastChartRow[] {
  const s = (summary || {}) as Summary;
  const { years, epsSeries, revenueSeries, priceSeries } = pickChartYears(s);

  const rows: ForecastChartRow[] = years.map((year, i) => ({
    year,
    eps: finiteOrNull(epsSeries[i]),
    revenue: finiteOrNull(revenueSeries[i]),
    price: finiteOrNull(priceSeries[i]),
    projected: false,
  }));

  const revenueForecast = buildRevenueForecast(summary, forecast);

  if (forecast.available && forecast.bull && forecast.base && forecast.bear) {
    rows.push({
      year: "Next Year",
      // EPS's projected value is identical across Bull/Base/Bear - all
      // three share the same growth-rate assumption (only the PE multiple
      // differs, and PE only affects price), so there is exactly one EPS
      // number to show here, not three overlapping copies of the same
      // figure. Revenue is likewise a single number, from its own CAGR.
      eps: forecast.base.projectedEps,
      revenue: revenueForecast?.projectedRevenue ?? null,
      price: null,
      projected: true,
      priceBull: forecast.bull.projectedPrice,
      priceBase: forecast.base.projectedPrice,
      priceBear: forecast.bear.projectedPrice,
    });
  }
  return rows;
}

/** Data-quality warnings port. */
export function dataQualityWarnings(summary: Summary | null): string[] {
  const s = (summary || {}) as Summary;
  const warnings: string[] = [];
  if (s.missing_emp_exp) warnings.push("Employee Benefit Expense is missing. SGA / Operating Profit may be understated.");
  if (s.missing_other_exp) warnings.push("Other Expenses are missing. SGA / Operating Profit may be understated.");
  if (s.missing_roce_direct) warnings.push("Direct ROCE data was unavailable, so the safer fallback formula is being used.");
  if (s.missing_working_cap_days) warnings.push("Working Capital Days is missing.");
  if (s.missing_cfo) warnings.push("Cash Flow from Operations is missing.");
  if (s.missing_capex) warnings.push("Capital Expenditure is missing.");
  if (s.missing_inv_days) warnings.push("Inventory Days is missing.");
  if (s.missing_deb_days) warnings.push("Debtor Days is missing.");
  if (s.missing_reserves) warnings.push("Reserves are missing.");
  const realYears = s.real_year_count;
  if (typeof realYears === "number" && realYears > 0 && realYears < 5) {
    const padded = 5 - realYears;
    warnings.push(
      `Only ${realYears} of the 5 years shown are real reported data - the other ${padded} ${padded === 1 ? "is" : "are"} zero-filled placeholders (this company has under 5 years of history). Trend charts and year-over-year comparisons for the padded years aren't meaningful.`
    );
  }
  const sourceDetail = String(s.data_source_detail || "");
  if (sourceDetail.includes("+")) {
    warnings.push(
      "Some figures come from consolidated filings and others from standalone filings (whichever had real data for that line item and year). A year-over-year change on a metric that switched basis may partly reflect that switch, not real business change."
    );
  }
  return warnings;
}
